import { useState, useEffect } from 'react';
import { useGetInstanceLogs } from '@/pages/company-profile/checklist/useChecklistLog';
import LogHistoryTable from './LogHistoryTable';
import { LogTableSkeleton } from './LogTableSkeleton';
import { AlertCircle } from 'lucide-react';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import SearchBar from '@/components/company/filters/SearchBar';
import FilterSelect from '@/components/company/filters/FilterSelect';
import ClearFilterButton from '@/components/company/filters/ClearFilterButton';

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

interface ChecklistLogProps {
  tenantId: string | number;
  instanceId: number;
}

const actionOptions = [
  { label: 'Konfirmasi ditambahkan', value: 'confirmation_added' },
  { label: 'Konfirmasi diupdate', value: 'confirmation_updated' },
  { label: 'Response dibuat', value: 'response_created' },
  { label: 'Response diupdate', value: 'response_updated' },
  { label: 'Merubah Status Checklist', value: 'response_status_changed' },
  { label: 'Response selesai', value: 'response_completed' },
  { label: 'Dokumen ditambahkan', value: 'response_documents_added' },
  { label: 'Dokumen dihapus', value: 'response_documents_removed' },
  { label: 'Media ditambahkan', value: 'response_media_added' },
  { label: 'Media dihapus', value: 'response_media_removed' },
  { label: 'Pengingat mingguan dikirim', value: 'weekly_reminder_sent' },
];

export const ChecklistLog = ({ tenantId, instanceId }: ChecklistLogProps) => {
  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'created_at',
    order: 'desc',
  });

  const [search, setSearch] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const {
    data: historyData,
    isLoading,
    isError,
  } = useGetInstanceLogs({
    tenantId,
    instanceId,
    page: pageConfig.page + 1,
    pageSize: pageConfig.limit,
    order: pageConfig.order,
    orderBy: pageConfig.orderBy,
    search: debouncedSearch,
    action: selectedAction,
  });

  if (isLoading) {
    return <LogTableSkeleton />;
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Failed to load logs</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-start mt-5 px-5 gap-2 mb-4">
        <SearchBar value={search} onChange={setSearch} placeholder="Cari log aktivitas..." className="w-full md:w-72" />
        <FilterSelect label="Aksi" options={actionOptions} value={selectedAction} onChange={setSelectedAction} />
        <ClearFilterButton
          onClear={() => {
            setSearch('');
            setSelectedAction('');
          }}
        />
      </div>
      {!historyData || historyData.logs.length === 0 ? (
        <div className="text-center py-12">
          <p className="font-medium dark:text-gray-100">Tidak ada log aktivitas</p>
          <p className="text-sm mt-2 dark:text-gray-400">Aktivitas akan dicatat di sini</p>
        </div>
      ) : (
        <LogHistoryTable
          isLoading={isLoading}
          logs={historyData.logs}
          totalItems={historyData.pagination.total_items}
          totalPages={historyData.pagination.total_pages}
          pageConfig={pageConfig}
          setPageConfig={setPageConfig}
        />
      )}
    </div>
  );
};
